import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, throttle } from 'throttle-debounce';
import type { NavigationProps } from '@sola/schema/lib/schema.ts';
import { useNavigationHover } from '../../hooks/use-navigation-hover.ts';
import type { NavLevel } from '../../molecules/nav-stack/nav-stack.tsx';
import { bodyScrollLock } from '../../utils/body-scroll-lock.ts';
type UseHeaderLogicParams = {
  menuItems: NavigationProps[];
};
export type UseHeaderLogicReturn = {
  // Menu state
  menuOpen: string;
  navKey: number;
  navStack: NavLevel[];
  setNavStack: React.Dispatch<React.SetStateAction<NavLevel[]>>;
  isFullscreenMenuOpen: boolean;
  headerHidden: boolean;

  // Refs (forwarded to JSX)
  refs: {
    mobileCloseButtonRef: React.RefObject<HTMLButtonElement | null>;
    mobileOpenButtonRef: React.RefObject<HTMLButtonElement | null>;
    accountTriggerRef: React.RefObject<HTMLButtonElement | null>;
    hamburgerTriggerRef: React.RefObject<HTMLButtonElement | null>;
  };

  // Navigation hover (pass-through from useNavigationHover)
  hoverHandlers: {
    activeItem: string;
    setActiveItem: (item: string) => void;
    handleTriggerEnter: (itemTitle: string) => void;
    handleTriggerLeave: (e?: React.PointerEvent) => void;
    handleContentEnter: () => void;
    handleContentLeave: () => void;
    handleViewportEnter: () => void;
    handleViewportLeave: () => void;
    resetNavigation: () => void;
  };

  // Callbacks
  callbacks: {
    handlePathnameChange: () => void;
    handleMobileClose: () => void;
    handleMobileBack: () => void;
    handleAccountOpenFromHamburger: () => void;
    handleActionNavValueChange: (value: string) => void;
  };
};
export const useHeaderLogic = ({
  menuItems
}: UseHeaderLogicParams): UseHeaderLogicReturn => {
  const [menuOpen, setMenuOpen] = useState('');
  const [navKey, setNavKey] = useState(0);
  const [navStack, setNavStack] = useState<NavLevel[]>([{
    items: menuItems
  }]);
  const isMobileMenuOpen = menuOpen === 'mobile';
  const isAccountMenuOpen = menuOpen === 'account';
  const isFullscreenMenuOpen = isMobileMenuOpen || isAccountMenuOpen;
  const [headerHidden, setHeaderHidden] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTopRef = useRef(0);
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
  const mobileOpenButtonRef = useRef<HTMLButtonElement>(null);
  const accountTriggerRef = useRef<HTMLButtonElement>(null);
  const hamburgerTriggerRef = useRef<HTMLButtonElement>(null);
  const accountOpenedFromHamburgerRef = useRef(false);
  const previousMenuRef = useRef('');
  const {
    activeItem,
    setActiveItem,
    handleTriggerEnter,
    handleTriggerLeave,
    handleContentEnter,
    handleContentLeave,
    handleViewportEnter,
    handleViewportLeave,
    resetNavigation
  } = useNavigationHover({
    openDelay: 300,
    switchDelay: 300,
    exitDelay: 500
  });

  // Pathname change handler — resets all menu state
  const handlePathnameChange = useCallback(() => {
    setMenuOpen('');
    setNavStack([{
      items: menuItems
    }]);
    resetNavigation();
    // Bump the key so Radix NavigationMenu roots remount cleanly
    // with no stale open/close animation state.
    setNavKey(k => k + 1);
  }, [menuItems, resetNavigation]);

  // Mobile close — resets navStack to root, resets navigation, closes menu
  const handleMobileClose = useCallback(() => {
    setNavStack([{
      items: menuItems
    }]);
    resetNavigation();
    setMenuOpen('');
  }, [menuItems, resetNavigation]);

  // Mobile back — pops the last level from navStack
  const handleMobileBack = useCallback(() => {
    setNavStack(prev => prev.slice(0, -1));
  }, []);

  // Account open from hamburger — sets ref flag and switches to account menu
  const handleAccountOpenFromHamburger = useCallback(() => {
    accountOpenedFromHamburgerRef.current = true;
    setMenuOpen('account');
  }, []);

  // Action nav value change — the onValueChange handler from the action NavigationMenu.Root
  const handleActionNavValueChange = useCallback((value: string) => {
    const wasFullscreen = menuOpen === 'mobile' || menuOpen === 'account';
    setMenuOpen(value);
    if (value === 'mobile' || value === 'account' || wasFullscreen) {
      resetNavigation();
    }
  }, [menuOpen, resetNavigation]);

  // Scroll-hide throttle logic
  const throttledHandleScroll = useMemo(() => throttle(250, () => {
    if (menuOpen === 'mobile' || menuOpen === 'account' || activeItem) {
      return;
    }
    resetNavigation();
    const reappearTimeout = 5000; // 5 seconds
    const scrollTop = window.scrollY || document.documentElement.scrollTop;

    // check > 80 to prevent whitespace behind the navbar appearing and mitigate rubberbanding on iOS
    if (scrollTop > lastScrollTopRef.current && scrollTop > 80) {
      setHeaderHidden(true);
    } else {
      setHeaderHidden(false);
    }
    lastScrollTopRef.current = scrollTop;
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setHeaderHidden(false);
    }, reappearTimeout);
  }), [menuOpen, activeItem, resetNavigation]);

  // Mobile menu open/close + scroll lock
  useEffect(() => {
    if (isMobileMenuOpen) {
      previousMenuRef.current = 'mobile';
      const debouncedFocus = debounce(100, () => {
        mobileCloseButtonRef.current?.focus();
      });
      debouncedFocus();
      bodyScrollLock(true, lastScrollTopRef.current);
      return;
    }
    if (previousMenuRef.current === 'mobile') {
      const debouncedFocus_0 = debounce(200, () => {
        hamburgerTriggerRef.current?.focus();
        previousMenuRef.current = '';
      });
      debouncedFocus_0();
      bodyScrollLock(false, lastScrollTopRef.current);
    }
  }, [isMobileMenuOpen]);

  // Account menu open/close + scroll lock
  useEffect(() => {
    if (isAccountMenuOpen) {
      previousMenuRef.current = 'account';
      bodyScrollLock(true, lastScrollTopRef.current);
      return;
    }
    if (previousMenuRef.current === 'account') {
      const debouncedFocus_1 = debounce(200, () => {
        if (accountOpenedFromHamburgerRef.current) {
          accountOpenedFromHamburgerRef.current = false;
          hamburgerTriggerRef.current?.focus();
        } else {
          accountTriggerRef.current?.focus();
        }
        previousMenuRef.current = '';
      });
      debouncedFocus_1();
      bodyScrollLock(false, lastScrollTopRef.current);
    }
  }, [isAccountMenuOpen]);

  // Scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', throttledHandleScroll);
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current); // Cleanup timeout on unmount
      }
    };
  }, [throttledHandleScroll]);

  // activeItem → menuOpen sync effect
  useEffect(() => {
    if (menuOpen === 'mobile' || menuOpen === 'account') {
      return;
    }
    if (activeItem) {
      setMenuOpen(activeItem);
      return;
    }
    setMenuOpen('');
  }, [activeItem, menuOpen]);
  return {
    menuOpen,
    navKey,
    navStack,
    setNavStack,
    isFullscreenMenuOpen,
    headerHidden,
    refs: {
      mobileCloseButtonRef,
      mobileOpenButtonRef,
      accountTriggerRef,
      hamburgerTriggerRef
    },
    hoverHandlers: {
      activeItem,
      setActiveItem,
      handleTriggerEnter,
      handleTriggerLeave,
      handleContentEnter,
      handleContentLeave,
      handleViewportEnter,
      handleViewportLeave,
      resetNavigation
    },
    callbacks: {
      handlePathnameChange,
      handleMobileClose,
      handleMobileBack,
      handleAccountOpenFromHamburger,
      handleActionNavValueChange
    }
  };
};